import { Divider, Flex, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { TransportationTypeEn } from 'src/constants/transportationType.enum';
import urlPath from 'src/constants/urlPath';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { useBasePath } from 'src/hooks/useBasePath';
import { IAdditionalWorkingTimeTicket } from 'src/types/hr_ticket/additionalWorkingTimeTicket.type';
import { IAdvancePaymentTicket } from 'src/types/hr_ticket/advancePaymentTicket.type';
import { IBusinessTripTicket } from 'src/types/hr_ticket/businessTripTicket.type';
import { IEquipmentSupplyRequestTicket } from 'src/types/hr_ticket/equipmentSupplyRequestTicket.type';
import { ILeavePackage } from 'src/types/hr_ticket/leavePackage.type';
import { ILeaveTicket } from 'src/types/hr_ticket/leaveTicket.type';
import { IOvertimeTicket } from 'src/types/hr_ticket/overtimeTicket.type';
import { IPurchaseRequestTicket } from 'src/types/hr_ticket/purchaseRequestTicket.type';
import { IResignationTicket } from 'src/types/hr_ticket/resignationTicket.type';
import { ITransportationTicket } from 'src/types/hr_ticket/transportationTicket.type';
import { AdditionalWorkingTimeAttachment, LeaveAttachment, TransportationAttachment } from './Attachment';
import TimelineApproval from './TimelineApproval';

const { Text } = Typography;

export default function HrSidebar() {
  const {
    leaveTicket,
    leavePackage,
    advancePaymentTicket,
    overtimeTicket,
    transportationTicket,
    businessTripTicket,
    purchaseRequestTicket,
    equipmentSupplyRequestTicket,
    additionalWorkingTimeTicket,
    resignationTicket
  } = useContext(HrTicketDetailContext);
  const [hrTicket, setHRTicket] = useState<
    | AxiosResponse<
        | ILeaveTicket
        | ILeavePackage
        | IBusinessTripTicket
        | IOvertimeTicket
        | IAdvancePaymentTicket
        | ITransportationTicket
        | IPurchaseRequestTicket
        | IEquipmentSupplyRequestTicket
        | IAdditionalWorkingTimeTicket
        | IResignationTicket,
        any
      >
    | undefined
  >();
  const basePath = useBasePath();
  //LeaveTicket
  const isMatchLeaveTicketPath =
    (basePath?.includes(urlPath.leaveTicket.location) ||
      basePath?.includes(urlPath.leaveTicketSub.location) ||
      basePath?.includes(urlPath.hrConfirmLeaveTicket.location)) &&
    !basePath?.includes(urlPath.leavePackageApproval.location);
  //LeavePackage
  const isMatchLeavePackagePath =
    basePath?.includes(urlPath.leavePackageApproval.location) || basePath?.includes(urlPath.myLeavePackage.location);
  //AdvancePayment
  const isMatchAdvancePaymentPath = basePath?.includes(urlPath.advancePaymentTicket.location);
  //OvertimeTicket
  const isMatchOvertimeTicketPath =
    basePath?.includes(urlPath.overtimeTicket.location) || basePath?.includes(urlPath.hrConfirmOvertimeTicket.location);
  //Transportation
  const isMatchTransportationTicketPath =
    basePath?.includes(urlPath.transportationTicket.location) ||
    basePath?.includes(urlPath.hrConfirmTransportationTicket.location);
  //BusinessTrip
  const isMatchBusinessTripTicketPath =
    basePath?.includes(urlPath.businessTripTicket.location) ||
    basePath?.includes(urlPath.hrConfirmBusinessTripTicket.location);
  //PurchaseRequest
  const isMatchPurchaseRequestTicketPath = basePath?.includes(urlPath.purchaseRequestTicket.location);
  //EquipmentSupplyRequest
  const isMatchEquipmentSupplyRequestTicketPath = basePath?.includes(urlPath.equipmentSupplyRequestTicket.location);
  //AdditionalWorkingTimeTicket
  const isMatchAdditionalWorkingTimeTicketPath = basePath?.includes(urlPath.myAdditionalWorkingTimeTicket.location);
  //AdditionalWorkingTimeTicket
  const isMatchAdditionalWorkingTimeTicketApprovalPath =
    basePath?.includes(urlPath.additionalWorkingTimeTicketApproval.location) ||
    basePath?.includes(urlPath.myAdditionalWorkingTimeTicket.location);
  // ResignationTicket
  const isMatchResignationTicket =
    basePath?.includes(urlPath.resignationTicket.location) ||
    basePath?.includes(urlPath.resignationTicketSub.location) ||
    basePath?.includes(urlPath.hrConfirmResignationTicket.location);

  const ticketType = isMatchResignationTicket ? 'resignation' : '';

  const tickets = useMemo(
    () => [
      { isMatchPath: isMatchLeavePackagePath, ticket: leavePackage },
      { isMatchPath: isMatchLeaveTicketPath, ticket: leaveTicket },
      { isMatchPath: isMatchAdvancePaymentPath, ticket: advancePaymentTicket },
      { isMatchPath: isMatchOvertimeTicketPath, ticket: overtimeTicket },
      { isMatchPath: isMatchTransportationTicketPath, ticket: transportationTicket },
      { isMatchPath: isMatchBusinessTripTicketPath, ticket: businessTripTicket },
      { isMatchPath: isMatchPurchaseRequestTicketPath, ticket: purchaseRequestTicket },
      { isMatchPath: isMatchEquipmentSupplyRequestTicketPath, ticket: equipmentSupplyRequestTicket },
      { isMatchPath: isMatchAdditionalWorkingTimeTicketPath, ticket: additionalWorkingTimeTicket },
      { isMatchPath: isMatchAdditionalWorkingTimeTicketApprovalPath, ticket: additionalWorkingTimeTicket },
      { isMatchPath: isMatchResignationTicket, ticket: resignationTicket }
    ],
    [
      additionalWorkingTimeTicket,
      advancePaymentTicket,
      businessTripTicket,
      equipmentSupplyRequestTicket,
      isMatchAdditionalWorkingTimeTicketApprovalPath,
      isMatchAdditionalWorkingTimeTicketPath,
      isMatchAdvancePaymentPath,
      isMatchBusinessTripTicketPath,
      isMatchEquipmentSupplyRequestTicketPath,
      isMatchLeavePackagePath,
      isMatchLeaveTicketPath,
      isMatchOvertimeTicketPath,
      isMatchPurchaseRequestTicketPath,
      isMatchTransportationTicketPath,
      isMatchResignationTicket,
      leaveTicket,
      leavePackage,
      overtimeTicket,
      purchaseRequestTicket,
      transportationTicket,
      resignationTicket
    ]
  );

  useEffect(() => {
    const matchedTicket = tickets.find(({ isMatchPath }) => isMatchPath);
    if (matchedTicket) {
      setHRTicket(matchedTicket.ticket);
    }
  }, [tickets]);

  return (
    <Fragment>
      {
        <Flex gap={30} vertical>
          <Flex gap={10} vertical>
            <Divider style={{ marginTop: '8px' }} orientation='left'>
              <Text strong>Hoạt động chính</Text>
            </Divider>
            <TimelineApproval hrTicket={hrTicket} ticketType={ticketType} />
          </Flex>
          {isMatchLeaveTicketPath && <LeaveAttachment />}
          {isMatchTransportationTicketPath &&
            transportationTicket?.data.transportationTypeId === TransportationTypeEn.selfSufficient && (
              <TransportationAttachment />
            )}
          {(isMatchAdditionalWorkingTimeTicketPath || isMatchAdditionalWorkingTimeTicketApprovalPath) && (
            <AdditionalWorkingTimeAttachment />
          )}
        </Flex>
      }
    </Fragment>
  );
}
